import { cn } from "@instacover/ui"
import { useParams } from "@tanstack/react-router"
import { clamp } from "lodash-es"
import { PropsWithChildren } from "react"
import { useTranslation } from "react-i18next"

import { isRealOrderedStep, useOrderedSteps } from "@/services/dexie"

import { useTheme } from "../../styles/themes/themes"
import { LocalesSelect } from "../LocalesSelect"

type Props = {
  hideLogo?: boolean
  hideCounter?: boolean
  hideLocales?: boolean
  dataTestId?: string
}

export function BaseLayout(props: PropsWithChildren<Props>) {
  const { children, hideLogo, hideCounter, hideLocales, dataTestId } = props
  const { sessionId } = useParams({ strict: false })

  const { activeTheme } = useTheme()

  return (
    <div
      className={cn("h-vh flex flex-col items-start", hideLogo ? "[--header-height:0px]" : "[--header-height:52px]")}
      data-testid={dataTestId}
    >
      <header className="grid h-[var(--header-height)] w-full shrink-0 grid-cols-[1fr_auto_1fr] items-center px-8">
        {!hideLogo && (
          <>
            <div className="col-start-1 flex h-11 items-center justify-self-start">
              <img
                // kvuli service workeru - jinak se nekesuje
                crossOrigin="anonymous"
                src={activeTheme.logoUrl}
                className="block h-9 max-w-45 max-[400px]:max-w-30"
              />
            </div>

            {!hideCounter && sessionId && <StepsCounter sessionId={sessionId} />}
          </>
        )}

        <div className="col-start-3 justify-self-end">{!hideLocales && <LocalesSelect />}</div>
      </header>

      <main className="flex-1 self-stretch">{children}</main>
    </div>
  )
}

type StepsCounterProps = {
  sessionId: string
}

function StepsCounter(props: StepsCounterProps) {
  const { sessionId } = props
  const { t } = useTranslation()
  const { data: orderedSteps } = useOrderedSteps(sessionId)

  const realSteps = (orderedSteps ?? []).filter(isRealOrderedStep)
  const totalSteps = realSteps.length
  const currentStep = clamp(1 + realSteps.filter((s) => s.isFinished).length, 1, totalSteps)

  if (currentStep === 0 || totalSteps === 0) {
    return null
  }

  return (
    <div className="col-start-2 flex flex-col items-center justify-center justify-self-center rounded-[22px] border border-black px-5 py-1 text-center">
      <div className="text-ic-heading text-xs leading-3 font-bold">{t("guide.step")}</div>

      <div className="text-ic-primary text-base leading-4 font-bold">
        {currentStep} / {totalSteps}
      </div>
    </div>
  )
}
